import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { authClient } from '../lib/authClient.js';
import { CONFIRMATION_STATE_KEY, type ConfirmationOutcome } from '../lib/confirmationLink.js';
import { useSession } from '../session/SessionProvider.js';

import { Alert, Button } from './ui.js';

type ResendState = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * What a confirmation link did, told to somebody already signed in - C-124,
 * Craig's ruling of 30 September 2026.
 *
 * **Shown once.** The outcome arrives in history state from `RequireAnonymous`,
 * and is taken out of it on the first render: a browser keeps history state
 * across a reload, so leaving it there would show the message again every time
 * the dashboard was reloaded.
 *
 * Wording is Craig's to change at the recheck.
 */
export function ConfirmationLinkNotice(): React.JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, refresh } = useSession();

  const [outcome] = useState<ConfirmationOutcome | null>(() => {
    const state = location.state as Record<string, unknown> | null;
    const value = state?.[CONFIRMATION_STATE_KEY];
    return value === 'confirmed' || value === 'failed' ? value : null;
  });
  const [dismissed, setDismissed] = useState(false);
  const [resend, setResend] = useState<ResendState>('idle');

  useEffect(() => {
    if (!outcome) return;
    // Out of history state at once, so a reload does not show it again.
    void navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // The session still says "unconfirmed" until it is read again.
    if (outcome === 'confirmed') void refresh();
    // Once, on arrival: the outcome never changes after the first render.
  }, []);

  if (!outcome || dismissed || !session) return null;

  async function sendNewLink(): Promise<void> {
    if (!session) return;
    setResend('sending');
    try {
      // The sign-in page's resend path, for the address this session signs in with.
      const result = await authClient.sendVerificationEmail({ email: session.user.email });
      setResend(result.error ? 'failed' : 'sent');
    } catch {
      setResend('failed');
    }
  }

  const dismiss = (
    <button
      type="button"
      onClick={() => {
        setDismissed(true);
      }}
      className="ml-auto shrink-0 text-sm font-medium underline"
    >
      Dismiss
    </button>
  );

  if (outcome === 'confirmed' || session.user.emailVerified) {
    return (
      <Alert tone="success">
        <div className="flex items-start gap-4">
          <p>
            {outcome === 'confirmed'
              ? 'Your email address is confirmed.'
              : 'Your email address is already confirmed.'}
          </p>
          {dismiss}
        </div>
      </Alert>
    );
  }

  return (
    <Alert>
      <div className="flex items-start gap-4">
        <div>
          <p>That confirmation link didn&rsquo;t work. It may have expired or already been used.</p>
          {resend === 'sent' ? (
            <p className="mt-2">
              A new confirmation link is on its way to <strong>{session.user.email}</strong>.
            </p>
          ) : resend === 'failed' ? (
            <p className="mt-2">We could not send it just now. Please try again in a moment.</p>
          ) : null}
          {resend === 'sent' ? null : (
            <div className="mt-3">
              <Button
                variant="secondary"
                busy={resend === 'sending'}
                onClick={() => void sendNewLink()}
              >
                Send a new link
              </Button>
            </div>
          )}
        </div>
        {dismiss}
      </div>
    </Alert>
  );
}
